/**
 * The tenant's portfolio at a glance: four counts, the workflow spread, and
 * what has happened lately.
 *
 * @module components/overview/PortfolioMetrics
 */

import React, { useState } from "react";

import { auditActorLabel } from "@/components/shared/audit-actor";
import { type AuditNames, auditTargetLabel } from "@/components/shared/audit-target";
import { AuditEventRow } from "@/components/shared/AuditEventRow";
import { workflowStateTone } from "@/components/shared/status-tones";
import { WORKFLOW_DISPLAY_ORDER } from "@/components/shared/workflow-queue-logic";
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Panel,
  ProgressBar,
  StatCard,
  StatGrid,
  Text,
} from "@/components/ui";
import { BarBones, ListBones } from "@/components/ui/SkeletonParts";
import type { PortfolioSummary, ProgrammeSummary, UserSummary } from "@/platform";

export interface PortfolioMetricsProps {
  portfolio: PortfolioSummary | null;
  programmes: readonly ProgrammeSummary[];
  users: readonly UserSummary[];
  error?: Error | null;
  onOpenProgramme?: (programmeId: string) => void;
  /** Names a tenant or settings target in the activity feed. */
  tenantName?: string;
}

/** How many recent events are worth showing before the list stops informing. */
const RECENT_EVENT_LIMIT = 5;

/**
 * @example
 * ```tsx
 * <PortfolioMetrics portfolio={platformPortfolio} programmes={…} users={…} />
 * ```
 */
export function PortfolioMetrics({
  portfolio,
  programmes,
  users,
  error = null,
  onOpenProgramme = undefined,
  tenantName = "This tenant",
}: PortfolioMetricsProps): React.ReactElement {
  const [showAllEvents, setShowAllEvents] = useState(false);
  const total = portfolio?.programmesTotal ?? programmes.length;
  const blocked = portfolio?.blockedOrStalledTotal ?? 0;
  const unassigned = portfolio?.unassignedModulesTotal ?? 0;
  const modulesTotal = portfolio?.modulesTotal ?? 0;

  const usersById = new Map(users.map((user) => [user.id, user.displayName]));
  const programmesById = new Map(programmes.map((programme) => [programme.id, programme]));
  const names: AuditNames = {
    users: usersById,
    programmes: new Map(programmes.map((programme) => [programme.id, programme.title])),
    modules: new Map(),
    tenantName,
  };

  const workflowBuckets = WORKFLOW_DISPLAY_ORDER.flatMap((state) => {
    const count = portfolio?.workflowCounts[state] ?? 0;
    return count > 0 ? [{ label: state, count, tone: workflowStateTone(state) }] : [];
  });

  const deliveryBuckets = [
    { label: "Active", count: portfolio?.deliveryCounts.active ?? 0 },
    { label: "Inactive", count: portfolio?.deliveryCounts.inactive ?? 0 },
    { label: "Retired", count: portfolio?.retiredTotal ?? 0 },
  ];

  /** Audit events name a target that may be a programme, a user, or neither. */
  const programmeFor = (target: string, metadata?: Record<string, unknown>): string | undefined => {
    if (programmesById.has(target)) {
      return target;
    }
    const fromMetadata = metadata?.programmeId;
    return typeof fromMetadata === "string" ? fromMetadata : undefined;
  };

  // Nothing reassuring unless the read actually answered. `?? 0` makes an
  // absent portfolio look like a fully staffed one, whether it is absent
  // because the read is still out or because it failed.
  const unanswered = Boolean(error) || portfolio === null;
  // Not answered *yet* is a skeleton; not answered because it failed is a dash.
  const loading = portfolio === null && !error;
  /** A count the read has not answered for is not zero. */
  const figure = (value: number): React.ReactNode => (unanswered ? "\u2014" : value);
  const staffingTone = unassigned > 0 ? "warning" : (unanswered && "neutral") || "success";
  const staffingFootnote =
    unassigned > 0
      ? `${unassigned} ${unassigned === 1 ? "module has" : "modules have"} no builder`
      : (!unanswered && "Every module is staffed") || undefined;

  return (
    <Panel title="Programme portfolio" icon="chart-bar-horizontal" collapsible>
      {error ? <Alert tone="warning">{error.message}</Alert> : null}

      <StatGrid columns={4} density="compact">
        <StatCard
          label="Total programmes"
          loading={loading && programmes.length === 0}
          // The one card with a real fallback: a known programme list is an
          // answer even when the portfolio read is not back. The dash is for
          // when neither has answered, which is every first paint.
          value={programmes.length === 0 ? figure(total) : total}
          footnote="In this tenant's portfolio"
          data-testid="portfolio-programmes-total"
        />
        <StatCard
          label="Awaiting decision"
          loading={loading}
          value={figure(portfolio?.awaitingDecisionTotal ?? 0)}
          footnote="In review or awaiting QA approval"
          data-testid="portfolio-awaiting-decision-total"
        />
        <StatCard
          label="Blocked or stalled"
          loading={loading}
          value={figure(blocked)}
          tone={blocked > 0 ? "warning" : "neutral"}
          footnote={`Changes required, or idle for 30 days${blocked > 0 ? " · Needs attention" : ""}`}
          data-testid="portfolio-blocked-stalled-total"
        />
        <StatCard
          // Stated as the staffed count, not the unstaffed one. A meter fills
          // toward good, so "12 of 13 without a builder" drew a nearly-full
          // bar over the worst possible reading, and `12 / 13` said twelve
          // done with one to go. The footnote carries the number that needs
          // acting on.
          label="Modules with builders"
          loading={loading}
          value={figure(modulesTotal - unassigned)}
          tone={staffingTone}
          // Dropped with the figure: a meter needs both halves, and "0 / 0"
          // is the same claim the em dash exists to refuse.
          total={unanswered ? undefined : modulesTotal}
          footnote={["Modules with a builder assigned", staffingFootnote]
            .filter(Boolean)
            .join(" · ")}
          data-testid="portfolio-module-staffing"
        />
      </StatGrid>

      <div className="mt-6 grid gap-4 xl:grid-cols-[1.2fr_0.8fr] [&>*]:min-w-0">
        <Panel
          tone="inset"
          density="compact"
          headingLevel={3}
          title="Recent governed activity"
          description="Audit events this account may see."
          data-testid="portfolio-recent-activity"
        >
          {loading ? <ListBones rows={3} label="Loading recent governed activity" /> : null}
          {!loading && portfolio?.recentAuditEvents.length ? (
            <>
              <ul className="flex flex-col">
                {(showAllEvents
                  ? portfolio.recentAuditEvents
                  : portfolio.recentAuditEvents.slice(0, RECENT_EVENT_LIMIT)
                ).map((event) => {
                  const programmeId = programmeFor(event.target, event.metadata);
                  const target =
                    (programmeId ? programmesById.get(programmeId)?.title : undefined) ??
                    auditTargetLabel(event, names);
                  return (
                    <li key={`${event.timestamp}-${event.actor}-${event.action}-${event.target}`}>
                      <AuditEventRow
                        action={event.action}
                        actor={auditActorLabel(event, usersById)}
                        subject={target}
                        timestamp={event.timestamp}
                        onOpen={
                          programmeId && onOpenProgramme
                            ? () => {
                                onOpenProgramme(programmeId);
                              }
                            : undefined
                        }
                        openLabel={target}
                      />
                    </li>
                  );
                })}
              </ul>
              {portfolio.recentAuditEvents.length > RECENT_EVENT_LIMIT ? (
                <Button
                  variant="link"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    setShowAllEvents((open) => !open);
                  }}
                >
                  {showAllEvents
                    ? "Show fewer"
                    : `Show ${portfolio.recentAuditEvents.length - RECENT_EVENT_LIMIT} more`}
                </Button>
              ) : null}
            </>
          ) : null}
          {!loading && !portfolio?.recentAuditEvents.length ? (
            <EmptyState
              title={
                unanswered
                  ? "Recent activity is not available yet"
                  : "No audit events are visible to this role"
              }
              density="row"
            />
          ) : null}
        </Panel>

        <div className="flex flex-col gap-4">
          <Panel
            tone="inset"
            density="compact"
            headingLevel={3}
            title="Workflow distribution"
            description="Shows the current status of the tenant's programmes."
          >
            {loading ? <BarBones rows={3} label="Loading workflow distribution" /> : null}
            {!loading && workflowBuckets.length === 0 ? (
              <EmptyState
                title={
                  unanswered
                    ? "The workflow distribution is not available yet"
                    : "No workflow data yet"
                }
                density="row"
              />
            ) : null}
            {!loading && workflowBuckets.length > 0 ? (
              <ul className="flex flex-col gap-3">
                {workflowBuckets.map((bucket) => {
                  const share = Math.round((bucket.count / Math.max(1, total)) * 100);
                  return (
                    <li key={bucket.label}>
                      <div className="flex items-baseline justify-between gap-3">
                        <Text as="span" className="font-medium">
                          {bucket.label}
                        </Text>
                        <Text as="span" size="sm" tone="muted">
                          {bucket.count} ({share}%)
                        </Text>
                      </div>
                      <ProgressBar
                        className="mt-1"
                        tone={bucket.tone}
                        value={bucket.count}
                        max={Math.max(1, total)}
                        label={`${bucket.label}: ${bucket.count} of ${total} programmes`}
                      />
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </Panel>

          <Panel
            tone="inset"
            density="compact"
            headingLevel={3}
            title="Delivery status"
            description="Shows delivery status, which is separate from design and approval."
          >
            {loading ? <BarBones rows={3} label="Loading delivery status" /> : null}
            {!loading && unanswered ? (
              <EmptyState title="Delivery status is not available yet" density="row" />
            ) : null}
            {unanswered ? null : (
              <ul className="flex flex-col gap-2">
                {deliveryBuckets.map((bucket) => (
                  <li key={bucket.label} className="flex items-center justify-between gap-3">
                    <Text as="span">{bucket.label}</Text>
                    <Badge tone="neutral" size="sm">
                      {bucket.count}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </Panel>
  );
}
